import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Button, SearchBar } from "@legal-500/legal500-design-system";
import { usePermissions } from "@/hooks/usePermissions";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useQuery } from "@tanstack/react-query";
import { researchCms, fetchAllEditionBpas } from "@/api/researchCms.service";
import type { EditionBpa } from "@/api/researchCms.service";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import {
  CmsEmptyState,
  CmsFilterRow,
  CmsGroupedTable,
  LanguageSwitcher,
  StatusBadge,
  reviewStatusBadge,
} from "../../../components/ResearchCms";
import {
  AddTargetModal,
  type AddTargetResult,
} from "../../../components/ResearchCms/AddTargetModal/AddTargetModal";
import { CmsLayout } from "./components/CmsLayout";
import { ArchivedBanner } from "./components/BpaImport/ArchivedBanner";
import { ImportHistoryList } from "./components/BpaImport/ImportHistoryList";
import { ImportDoneBanner } from "./components/BpaImport/ImportDoneBanner";
import { ImportedBadge } from "./components/BpaImport/ImportedBadge";
import { CmsBpaSplitDialog } from "./components/BpaSplit/CmsBpaSplitDialog";
import { readImportDone } from "./components/BpaImport/importDone";
import { archivedBannerModel } from "./utils/archivedBanner";
import { splitEntryState, splitHalves, splitHubUrl, type HistoryState } from "./utils/bpaSplit";
import {
  isInMerge,
  mergeBadge,
  mergeBanner,
  type MergedOutcome,
  mergedMessage,
  toMergeRow,
} from "./utils/bpaMerge";
import { ScheduledForMergeBadge } from "./components/BpaMerge/ScheduledForMergeBadge";
import bannerStyles from "./components/BpaImport/ImportBanners.module.css";
import importShared from "./components/BpaImport/importShared.module.css";
import { canImportContent } from "./utils/bpaImport";
import { importBlockedReason } from "./utils/bpaImportEditionStatus";
import { resolveManageTarget } from "./utils/resolveManageTarget";
import { toLawyerRows } from "./utils/lawyerRows";
import type { LawyerRow } from "./utils/lawyerRows";
import { filterLawyerRows } from "./utils/filterLawyerRows";
import { toOrgRows, submissionBadgeLabel } from "./utils/orgRows";
import {
  toOrgSections,
  orgSectionRowKey,
  toLawyerSections,
  lawyerSectionRowKey,
  UNTABLED_SECTION_KEY,
} from "./utils/orgSections";
import type { OrgSectionRow, LawyerSectionRow } from "./utils/orgSections";
import { rankingBadgeLabel } from "./utils/rankingBadge";
import { formatRankingBadge } from "./utils/lawyerRankingBadge";
import { readOrgListFilters, writeOrgListFilters } from "./utils/orgListQuery";
import type { OrgListQueryFilters } from "./utils/orgListQuery";
import { countryFlag } from "./utils/countryFlag";
import { getTrafficLightColor } from "./utils/trafficLight";
import type { CmsNavState } from "./types";
import styles from "./CmsBpaPage.module.css";

type ActiveFilter = { key: string; label: string };

const TABS = [
  { id: "organisations", label: "Organisations" },
  { id: "lawyers", label: "Lawyers" },
] as const;

const TIER_FILTER_OPTIONS = [
  { value: "0", label: "Recommended (untiered)" },
  { value: "1", label: "Tier 1" },
  { value: "2", label: "Tier 2" },
  { value: "3", label: "Tier 3" },
  { value: "4", label: "Tier 4" },
  { value: "5", label: "Tier 5" },
  { value: "6", label: "Tier 6" },
  { value: "7", label: "Tier 7" },
  { value: "8", label: "Tier 8" },
];

const REVIEW_STATUS_FILTER_OPTIONS = [
  { value: "not_started", label: reviewStatusBadge(null).label },
  ...["pending", "ready_for_editing", "ready_for_translation", "ready_for_release"].map(status => ({
    value: status,
    label: reviewStatusBadge(status).label,
  })),
];

function readNames(locationState: unknown, key: string): string[] {
  if (typeof locationState !== "object" || locationState === null) return [];
  const names = (locationState as Record<string, unknown>)[key];
  if (!Array.isArray(names)) return [];
  return names.filter((name): name is string => typeof name === "string");
}

function readMergedSources(locationState: unknown): {
  sourceNames: string[];
  outcome: MergedOutcome;
} {
  const sourceNames = readNames(locationState, "mergedSources");
  return {
    sourceNames,
    outcome: {
      broughtIn: readNames(locationState, "mergedBroughtIn"),
      keptOut: readNames(locationState, "mergedKeptOut"),
    },
  };
}

function tierFilterLabel(tier: number): string {
  return tier === 0 ? "Recommended (untiered)" : `Tier ${tier}`;
}

function populatedPct(row: { populatedFields: number; totalFields: number }): number {
  return row.totalFields === 0 ? 0 : Math.round((row.populatedFields / row.totalFields) * 100);
}

function tierBadge(ranking: { rankingTier: number | null; rankingType: string | null }): ReactNode {
  const label = rankingBadgeLabel(ranking);
  return (
    <span className={label === "—" ? styles.rankingBadgeNone : styles.rankingBadge}>{label}</span>
  );
}

export function CmsBpaPage() {
  const { hasPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const cmsExportEnabled = hasFlag("research-cms-1-enabled");
  const { editionId: routeEditionId, bpaId: routeBpaId } = useParams<{
    editionId: string;
    bpaId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as CmsNavState;

  const editionId = routeEditionId!;
  const bpaId = routeBpaId!;

  const hasNavState = Boolean(navState?.bpaName);

  const {
    data: apiData,
    isLoading: apiLoading,
    error: apiError,
  } = useQuery({
    queryKey: ["research-bpa-detail", editionId, bpaId],
    queryFn: async () => {
      const [allBpas, editionsResult] = await Promise.all([
        fetchAllEditionBpas({ editionId }),
        researchCms.getEditions({ editionId, limit: 1 }),
      ]);

      let bpa = allBpas.find(b => b.bpaId === bpaId);
      let bpaStatus: string | null = bpa?.status ?? null;

      // Archived BPAs are filtered from the default list — check archived if not found
      if (!bpa) {
        const archivedResult = await researchCms.getEditionBpas({
          editionId,
          status: "archived",
        });
        bpa = archivedResult.editionBpas.find((b: EditionBpa) => b.bpaId === bpaId);
        if (bpa) bpaStatus = "archived";
      }

      const edition = editionsResult.editionConfigs[0];

      return {
        editionBpaId: bpa?.id ?? null,
        bpaName: bpa?.bpaName ?? null,
        bpaStatus,
        outputName: bpa?.outputName ?? null,
        englishName: bpa?.englishName ?? null,
        rankingEntityId: bpa?.rankingEntityId ?? null,
        rankingEntityName: bpa?.rankingEntityName ?? null,
        rankingEntityType: bpa?.rankingEntityType ?? null,
        editionName: edition?.editionName ?? null,
        directoryId: edition?.directoryId ?? null,
        editionStatus: edition?.status ?? null,
        mergeRows: allBpas.map(toMergeRow),
      };
    },
  });

  const editionBpaId = apiData?.editionBpaId ?? null;
  const bpaName = navState?.bpaName ?? apiData?.bpaName ?? "";
  const bpaStatus = apiData?.bpaStatus ?? null;
  const isArchived = bpaStatus === "archived";
  const outputName = apiData?.outputName ?? null;
  const englishName = apiData?.englishName ?? null;
  const hasTranslation = outputName !== bpaName || englishName !== bpaName;
  const rankingEntityId = apiData?.rankingEntityId ?? "";
  const rankingEntityName = navState?.rankingEntityName ?? apiData?.rankingEntityName ?? "";
  const rankingEntityType = apiData?.rankingEntityType ?? null;
  const editionName = navState?.editionName ?? apiData?.editionName ?? "";

  const { data: publicationsData } = useQuery({
    queryKey: ["research-publications-list"],
    queryFn: async () => (await researchCms.getPublications()).publications,
  });
  const manageTarget = useMemo(
    () => resolveManageTarget(publicationsData ?? [], editionId),
    [publicationsData, editionId],
  );
  const outputLanguage = useMemo(() => {
    for (const pub of publicationsData ?? []) {
      if (pub.editions.some(e => e.editionId === editionId)) return pub.outputLanguage;
    }
    return "en-GB";
  }, [publicationsData, editionId]);
  const canEdit = hasPermission("staff.research.edit");
  const canDelete = hasPermission("staff.research.delete");
  const canImport = canImportContent(cmsExportEnabled, hasPermission);
  const [importDismissed, setImportDismissed] = useState(false);
  const importDone = importDismissed ? null : readImportDone(location.state);
  const importedCompanyIds = useMemo(
    () => new Set(readImportDone(location.state)?.importedCompanyIds ?? []),
    [location.state],
  );
  const editionStatus = apiData?.editionStatus ?? null;
  const importBlocked = importBlockedReason(editionStatus);
  const templateType = navState?.templateType ?? "conventional";
  const [splitOpen, setSplitOpen] = useState(false);
  const [mergedDismissed, setMergedDismissed] = useState(false);
  const mergeRows = apiData?.mergeRows ?? [];
  const mergeRow = mergeRows.find(row => row.bpaId === bpaId) ?? null;
  const inMerge = mergeRow ? isInMerge(mergeRow) : false;
  const mergeOptions = { editionId, flagOn: cmsExportEnabled };
  const pageMergeBanner = mergeRow ? mergeBanner(mergeRow, mergeRows, mergeOptions) : null;
  const targetBadge =
    mergeRow && mergeRow.mergeSourceCount > 0
      ? mergeBadge(mergeRow, mergeRows, mergeOptions)
      : null;
  const merged = readMergedSources(location.state);

  const importHistoryQuery = useQuery({
    queryKey: ["import-history", editionBpaId],
    queryFn: () => researchCms.getImportHistory(editionBpaId!),
    enabled: !!editionBpaId && cmsExportEnabled,
  });
  const importHistoryData = importHistoryQuery.data;
  const historyState: HistoryState = importHistoryQuery.isError
    ? "failed"
    : importHistoryQuery.isSuccess
      ? "ready"
      : "loading";

  const splitHasMoved = (importHistoryData?.importHistory ?? []).some(
    entry => entry.purpose === "split" && entry.sourceEditionBpaId === editionBpaId,
  );
  const splitHalvesQuery = useQuery({
    queryKey: ["research-split-halves", editionId, rankingEntityId, editionBpaId],
    refetchOnMount: "always",
    queryFn: () =>
      fetchAllEditionBpas({ editionId, rankingEntityId, sourceEditionBpaId: editionBpaId! }),
    enabled: !!editionBpaId && !!rankingEntityId && cmsExportEnabled && canEdit,
  });
  const halves = splitHalves(
    { id: editionBpaId ?? "", rankingEntityId },
    splitHalvesQuery.data ?? [],
  );
  const splitEntry = splitEntryState({
    flagOn: cmsExportEnabled,
    canEdit,
    canDelete,
    archived: isArchived,
    editionStatus,
    splitHasMoved,
    inMerge,
    historyState,
  });

  function openSplit() {
    if (splitHasMoved) return navigate(splitHubUrl(editionId, bpaId, []));
    if (splitHalvesQuery.isFetching) return;
    if (splitHalvesQuery.isError) return void splitHalvesQuery.refetch();
    if (halves.length > 0) return navigate(splitHubUrl(editionId, bpaId, halves));
    setSplitOpen(true);
  }

  const { data: rankingTablesData } = useQuery({
    queryKey: ["research-ranking-tables", bpaId, editionId],
    queryFn: () => researchCms.getRankingTables({ bpaId: bpaId!, editionId: editionId! }),
  });
  // Memoised because the org sections are keyed off it: a fresh [] each render would rebuild them.
  const rankingTables = useMemo(() => rankingTablesData?.rankingTables ?? [], [rankingTablesData]);
  // The lawyer list groups only by lawyer tables — there is no lawyer equivalent of a firm table.
  const lawyerRankingTables = useMemo(
    () => rankingTables.filter(t => t.isLawyerTable === 1),
    [rankingTables],
  );
  // firm_recommended is the only firm table organisations submit into: firm_to_watch and
  // commentary carry no submission, so a lawyer-only practice area must not show Organisations.
  const showLawyersTab = rankingTables.some(t => t.isLawyerTable === 1);
  const showOrgsTab = rankingTables.some(t => t.subtype === "firm_recommended");
  const noRankingTables = rankingTablesData !== undefined && rankingTables.length === 0;

  const {
    data: orgData,
    isLoading: orgsLoading,
    error: orgsError,
  } = useQuery({
    queryKey: ["research-org-list", editionId, bpaId],
    queryFn: async () => {
      const result = await researchCms.getEditorialContentOrganisations({
        editionId,
        researchBpaId: bpaId,
        sort: "company_name",
      });
      const rows = toOrgRows(
        result.organisations.map(org => ({
          companyId: org.companyId,
          companyName: org.companyName,
          officeName: org.officeName ?? null,
          rowKind: org.rowKind ?? "primary",
          reviewStatus: org.reviewStatus,
          rankingTier: org.rankingTier ?? null,
          rankingType: org.rankingType ?? null,
          currentRankingTier: org.currentRankingTier ?? null,
          currentRankingType: org.currentRankingType ?? null,
          previousRankingTier: org.previousRankingTier ?? null,
          previousRankingType: org.previousRankingType ?? null,
          currentRankings: org.currentRankings ?? [],
          previousRankings: org.previousRankings ?? [],
          hasSubmission: org.hasSubmission,
          populatedFields: org.populatedFields,
          totalFields: org.totalFields,
          contentTableIds: org.contentTableIds ?? [],
          contentByTable: org.contentByTable ?? {},
        })),
      );
      return { rows, totalFieldsPerEntity: result.totalFieldsPerEntity };
    },
  });

  const organisations = orgData?.rows ?? [];
  const orgTotalFieldsPerEntity = orgData?.totalFieldsPerEntity ?? 0;

  const tierFilterOptions = useMemo(() => {
    const tiers = new Set<number>();
    for (const org of organisations) {
      if (org.currentRankingTier !== null) tiers.add(org.currentRankingTier);
      if (org.previousRankingTier !== null) tiers.add(org.previousRankingTier);
    }
    return TIER_FILTER_OPTIONS.filter(opt => tiers.has(Number(opt.value)));
  }, [organisations]);

  const {
    data: lawyerRows,
    isLoading: lawyersLoading,
    error: lawyersError,
  } = useQuery({
    queryKey: ["research-lawyer-list", editionId, bpaId],
    queryFn: async () => {
      const result = await researchCms.getEditorialContentIndividuals({
        editionId,
        researchBpaId: bpaId,
      });
      return {
        rows: toLawyerRows(result.individuals),
        totalFieldsPerEntity: result.totalFieldsPerEntity,
      };
    },
  });

  const lawyers = lawyerRows?.rows ?? [];
  const lawyerTotalFieldsPerEntity = lawyerRows?.totalFieldsPerEntity ?? 0;

  const [searchParams, setSearchParams] = useSearchParams();
  const showTranslation = searchParams.get("lang") === "en-GB";
  const displayName = showTranslation ? (englishName ?? bpaName) : (outputName ?? bpaName);

  const defaultTab = showOrgsTab ? "organisations" : "lawyers";
  const activeTab = searchParams.get("tab") ?? defaultTab;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const isOrgsTab = activeTab === "organisations";

  const openAddModal = useCallback(() => {
    setAddError(null);
    setAddModalOpen(true);
  }, []);

  const handleAddTarget = useCallback(
    async (target: AddTargetResult) => {
      if (!target.companyId) {
        setAddError("This contact is not linked to a company in Address Book.");
        return;
      }
      setAddModalOpen(false);
      const entityId = target.contactId ?? target.companyId;
      const route = target.contactId
        ? ROUTES.research.cmsIndividual(editionId, bpaId, entityId)
        : ROUTES.research.cmsOrg(editionId, bpaId, entityId);
      navigate(route, {
        state: {
          bpaName,
          rankingEntityId: rankingEntityId || undefined,
          rankingEntityName,
          editionName,
          companyName: target.name,
          entityName: target.name,
          officeId: target.officeId,
          officeName: target.officeName,
        },
      });
    },
    [editionId, bpaId, bpaName, rankingEntityId, rankingEntityName, editionName, navigate],
  );

  // Which submission tables each entity already has a row for, so the add modal can grey out
  // only an entity that occupies every one of them rather than the whole entity outright. Built
  // once here from the same contentTableIds the section-placement "already added" check and the
  // create payload both rely on.
  const existingTableIdsByEntityId = useMemo(
    () =>
      new Map(
        isOrgsTab
          ? organisations.map(o => [o.companyUuid, new Set(o.contentTableIds)] as const)
          : lawyers.map(l => [l.contactUuid, new Set(l.contentTableIds)] as const),
      ),
    [isOrgsTab, organisations, lawyers],
  );

  const searchQuery = searchParams.get("search") ?? "";
  const submissionFilter = searchParams.get("submission") ?? "";
  const sortBy = searchParams.get("sort") ?? "name_asc";

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setSearchParams],
  );

  const clearParam = useCallback((key: string) => setParam(key, ""), [setParam]);

  const orgListFilters = useMemo(() => readOrgListFilters(searchParams), [searchParams]);
  const tierFilter = orgListFilters.tier;
  const reviewStatusFilter = orgListFilters.reviewStatus ?? "";

  const setOrgListFilter = useCallback(
    (patch: Partial<OrgListQueryFilters>) => {
      setSearchParams(prev => {
        const current = readOrgListFilters(prev);
        return writeOrgListFilters(prev, { ...current, ...patch });
      });
    },
    [setSearchParams],
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        next.delete("search");
        next.delete("submission");
        next.delete("sort");
        return next;
      });
    },
    [setSearchParams],
  );

  const activeFilters = useMemo(() => {
    const pills: ActiveFilter[] = [];
    if (submissionFilter)
      pills.push({
        key: "submission",
        label: `Submission: ${submissionFilter === "true" ? "Submitted" : "No submission"}`,
      });
    if (isOrgsTab && tierFilter !== undefined)
      pills.push({
        key: "tier",
        label: `Ranking: ${tierFilterLabel(tierFilter)}`,
      });
    if (reviewStatusFilter)
      pills.push({
        key: "reviewStatus",
        label: `Review status: ${reviewStatusBadge(reviewStatusFilter).label}`,
      });
    return pills;
  }, [submissionFilter, tierFilter, reviewStatusFilter, isOrgsTab]);

  const filteredOrgs = useMemo(() => {
    let result = organisations.filter(org => {
      const matchesSearch =
        !searchQuery || org.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSubmission =
        !submissionFilter || (submissionFilter === "true" ? org.hasSubmission : !org.hasSubmission);
      // Tier 0 (the recommended-but-untiered band) is a real filter value, not "unset" — the
      // check is against undefined, not truthiness, so it isn't dropped. A firm matches on
      // either edition's tier, so a firm that dropped out of this edition's ranking is still
      // reachable by the tier it held last edition.
      const matchesTier =
        tierFilter === undefined ||
        org.currentRankingTier === tierFilter ||
        org.previousRankingTier === tierFilter;
      const matchesReviewStatus =
        !reviewStatusFilter ||
        org.reviewStatus === reviewStatusFilter ||
        (reviewStatusFilter === "not_started" && !org.reviewStatus) ||
        Object.values(org.contentByTable ?? {}).some(
          c =>
            c.reviewStatus === reviewStatusFilter ||
            (reviewStatusFilter === "not_started" && !c.reviewStatus),
        );
      return matchesSearch && matchesSubmission && matchesTier && matchesReviewStatus;
    });

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "progress_desc":
          return populatedPct(b) - populatedPct(a) || a.name.localeCompare(b.name);
        case "progress_asc":
          return populatedPct(a) - populatedPct(b) || a.name.localeCompare(b.name);
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return result;
  }, [organisations, searchQuery, submissionFilter, sortBy, tierFilter, reviewStatusFilter]);

  const filteredLawyers = useMemo(() => {
    let result = filterLawyerRows(lawyers, {
      search: searchQuery,
      submission: submissionFilter,
      reviewStatus: reviewStatusFilter,
    });

    // Ordering is by firm, because the lawyer's own name is a uuid until the column lands.
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case "name_desc":
          return b.organisationName.localeCompare(a.organisationName);
        case "progress_desc":
          return (
            populatedPct(b) - populatedPct(a) ||
            a.organisationName.localeCompare(b.organisationName)
          );
        case "progress_asc":
          return (
            populatedPct(a) - populatedPct(b) ||
            a.organisationName.localeCompare(b.organisationName)
          );
        default:
          return a.organisationName.localeCompare(b.organisationName);
      }
    });

    return result;
  }, [lawyers, searchQuery, submissionFilter, reviewStatusFilter, sortBy]);

  // A WordPress table id that names no configured table is a genuine research_wp_mapping gap
  // (not the expected pre-sync null-id state, which reports nothing) — logged so it can never
  // silently masquerade as "every ranking placed correctly".
  const warnUnmappedTableId = useCallback(
    (entity: string, wpTableIds: Set<number>) => {
      if (wpTableIds.size === 0) return;
      // biome-ignore lint/suspicious/noConsole: surface a genuine research_wp_mapping gap for diagnosis rather than masking it
      console.warn(
        `${entity} ranking(s) carry a WordPress table id with no research_wp_mapping row`,
        { bpaId, editionId, wpTableIds: [...wpTableIds] },
      );
    },
    [bpaId, editionId],
  );

  // Sections are built from the filtered rows, so a section a filter emptied is never rendered.
  const orgTableGroups = useMemo(() => {
    const unmappedWpIds = new Set<number>();
    const sections = toOrgSections(filteredOrgs, {
      tables: rankingTables,
      mainTableLabel: bpaName,
      totalFieldsPerEntity: orgTotalFieldsPerEntity,
      onUnmappedTableId: wpTableId => unmappedWpIds.add(wpTableId),
    });
    warnUnmappedTableId("Organisation", unmappedWpIds);
    return sections.map(s => {
      const tableId = s.rows[0]?.sectionKey;
      return {
        ...s,
        href:
          tableId && tableId !== UNTABLED_SECTION_KEY
            ? ROUTES.research.cmsRankingTable(editionId, bpaId, tableId)
            : undefined,
      };
    });
  }, [
    filteredOrgs,
    rankingTables,
    bpaName,
    orgTotalFieldsPerEntity,
    warnUnmappedTableId,
    editionId,
    bpaId,
  ]);
  const lawyerTableGroups = useMemo(() => {
    const unmappedWpIds = new Set<number>();
    const sections = toLawyerSections(filteredLawyers, {
      tables: lawyerRankingTables,
      mainTableLabel: bpaName,
      totalFieldsPerEntity: lawyerTotalFieldsPerEntity,
      onUnmappedTableId: wpTableId => unmappedWpIds.add(wpTableId),
    });
    warnUnmappedTableId("Lawyer", unmappedWpIds);
    return sections.map(s => {
      const tableId = s.rows[0]?.sectionKey;
      return {
        ...s,
        href:
          tableId && tableId !== UNTABLED_SECTION_KEY
            ? ROUTES.research.cmsRankingTable(editionId, bpaId, tableId)
            : undefined,
      };
    });
  }, [
    filteredLawyers,
    lawyerRankingTables,
    bpaName,
    lawyerTotalFieldsPerEntity,
    warnUnmappedTableId,
  ]);

  const clearAll = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("submission");
      next.delete("search");
      next.delete("sort");
      next.delete("tier");
      next.delete("reviewStatus");
      return next;
    });
  }, [setSearchParams]);

  const orgColumns = useMemo(
    () => [
      {
        key: "organisation",
        header: "Organisation",
        render: (org: OrgSectionRow) => (
          <div>
            <span className={styles.orgName}>{org.name}</span>
            {org.officeName && <span className={styles.officeName}>{org.officeName}</span>}
          </div>
        ),
      },
      {
        key: "tier",
        header: "Tier",
        width: "100px",
        // One tier, the one this section's table gives the firm — the section header names it.
        render: (org: OrgSectionRow) =>
          tierBadge({ rankingTier: org.sectionTier, rankingType: org.sectionRankingType }),
      },
      {
        key: "prevTier",
        header: "Prev tier",
        width: "100px",
        render: (org: OrgSectionRow) =>
          tierBadge({
            rankingTier: org.sectionPreviousTier,
            rankingType: org.sectionPreviousRankingType,
          }),
      },
      {
        key: "submission",
        header: "Submission",
        width: "230px",
        render: (org: OrgSectionRow) => {
          const hasAnySubmission = !org.sectionIsSubmittable
            ? Object.values(org.contentByTable).some(c => c.hasSubmission)
            : org.hasSubmission;
          const label = hasAnySubmission ? "Submitted" : "No submission";
          return (
            <div className={styles.submissionCell}>
              <span
                className={`${styles.submissionBadge} ${hasAnySubmission ? styles.submitted : styles.notSubmitted}`}
              >
                {label}
              </span>
              {importedCompanyIds.has(org.companyUuid) && <ImportedBadge />}
            </div>
          );
        },
      },
      {
        key: "reviewStatus",
        header: "Review status",
        width: "170px",
        render: (org: OrgSectionRow) => (
          <StatusBadge
            status={org.reviewStatus}
            vocabulary="review-status"
            editionStatus={editionStatus}
          />
        ),
      },
      {
        key: "progress",
        header: "Progress",
        width: "320px",
        render: (org: OrgSectionRow) => {
          const pct = populatedPct(org);
          const progressColor = getTrafficLightColor(pct);
          return (
            <div className={styles.progressCell}>
              <div className={styles.progressBar}>
                <div
                  className={styles.barFill}
                  style={{ width: `${pct}%`, background: progressColor }}
                />
              </div>
              <span
                className={styles.progressLabel}
                style={{ color: pct > 0 ? progressColor : undefined }}
              >
                {`${org.populatedFields} of ${org.totalFields} fields populated`}
              </span>
            </div>
          );
        },
      },
    ],
    [editionStatus, importedCompanyIds],
  );

  const lawyerColumns = useMemo(
    () => [
      {
        key: "name",
        header: "Lawyer",
        render: (lawyer: LawyerSectionRow) => (
          <div>
            <span className={styles.orgName}>{lawyer.name}</span>
            <span className={styles.lawyerOrg}>{lawyer.organisationName}</span>
          </div>
        ),
      },
      {
        key: "tier",
        header: "Tier",
        // Sized for the longest category name the lawyer formatter can render
        // ("Next Generation Partners"), not for a single digit. Task 0048 item 2.
        width: "150px",
        // One tier, the one this section's table gives the lawyer — the section header names it.
        render: (lawyer: LawyerSectionRow) => {
          // formatRankingBadge, not the org list's rankingBadgeLabel: this is the lawyer-grain
          // formatter, so it strips the stored `lawyer ` prefix from a category name the way
          // staff-ui.individual-view.6 requires, and renders tier 0 as its band rather than a 0.
          const label = formatRankingBadge(
            lawyer.sectionTier === null
              ? null
              : {
                  rankingTier: lawyer.sectionTier,
                  rankingType: lawyer.sectionRankingType,
                },
          );
          return (
            <span className={label === "—" ? styles.rankingBadgeNone : styles.rankingBadge}>
              {label}
            </span>
          );
        },
      },
      {
        key: "prevTier",
        header: "Prev tier",
        // Same rendering and sizing as the Tier column beside it: the previous tier can name the
        // same long categories the current one can.
        width: "150px",
        render: (lawyer: LawyerSectionRow) => {
          const label = formatRankingBadge(
            lawyer.sectionPreviousTier === null
              ? null
              : {
                  rankingTier: lawyer.sectionPreviousTier,
                  rankingType: lawyer.sectionPreviousRankingType,
                },
          );
          return (
            <span className={label === "—" ? styles.rankingBadgeNone : styles.rankingBadge}>
              {label}
            </span>
          );
        },
      },
      {
        key: "submission",
        header: "Submission",
        width: "150px",
        render: (lawyer: LawyerSectionRow) => {
          const label = submissionBadgeLabel(lawyer);
          return (
            <span
              className={`${styles.submissionBadge} ${label === "Submitted" ? styles.submitted : styles.notSubmitted}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        key: "reviewStatus",
        header: "Review status",
        width: "170px",
        render: (lawyer: LawyerSectionRow) => (
          <StatusBadge
            status={lawyer.reviewStatus}
            vocabulary="review-status"
            editionStatus={editionStatus}
          />
        ),
      },
      {
        key: "progress",
        header: "Progress",
        width: "320px",
        render: (lawyer: LawyerSectionRow) => {
          const pct = populatedPct(lawyer);
          const progressColor = getTrafficLightColor(pct);
          return (
            <div className={styles.progressCell}>
              <div className={styles.progressBar}>
                <div
                  className={styles.barFill}
                  style={{ width: `${pct}%`, background: progressColor }}
                />
              </div>
              <span
                className={styles.progressLabel}
                style={{ color: pct > 0 ? progressColor : undefined }}
              >
                {`${lawyer.populatedFields} of ${lawyer.totalFields} fields populated`}
              </span>
            </div>
          );
        },
      },
    ],
    [editionStatus],
  );

  if (!hasPermission("staff.research.read")) return <UnauthorizedView />;

  const entityLabel = isOrgsTab ? "organisations" : "lawyers";

  if (!hasNavState && apiLoading) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: "Loading...",
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Loading..." subtitle="" />
      </CmsLayout>
    );
  }

  const loadError = orgsError ?? (hasNavState ? null : apiError);

  if (loadError) {
    return (
      <CmsLayout
        breadcrumbs={[
          {
            label: editionName,
            path: ROUTES.research.cmsPublication(editionId),
          },
        ]}
      >
        <PageHeader title="Failed to load practice area" subtitle="" />
        <div className={styles.errorMessage} role="alert">
          {loadError.message}
        </div>
      </CmsLayout>
    );
  }

  return (
    <CmsLayout
      breadcrumbs={[
        { label: editionName, path: ROUTES.research.cmsPublication(editionId) },
        ...(rankingEntityName
          ? [
              {
                label: rankingEntityName,
                path: `${ROUTES.research.cmsPublication(editionId)}?rankingEntity=${rankingEntityId}`,
              },
            ]
          : []),
        { label: displayName, path: ROUTES.research.cmsBpa(editionId, bpaId) },
      ]}
    >
      <div className={styles.headerRow}>
        <div className={styles.headerTitle}>
          <div className={styles.titleRow}>
            <h1 className={styles.pageTitle}>{displayName}</h1>
            {isArchived && <StatusBadge status="archived" />}
            {!targetBadge && pageMergeBanner && !isArchived && (
              <ScheduledForMergeBadge label={pageMergeBanner.title} href={pageMergeBanner.href} />
            )}
            {targetBadge && (
              <ScheduledForMergeBadge label={targetBadge.label} href={targetBadge.href} />
            )}
            {rankingEntityName && (
              <>
                <span className={styles.regionBadge}>{rankingEntityName}</span>
                {(() => {
                  const flag = countryFlag(rankingEntityName);
                  return flag ? <span className={styles.regionFlag}>{flag}</span> : null;
                })()}
              </>
            )}
          </div>
          <p className={styles.pageSubtitle}>{editionName}</p>
        </div>
        <div className={styles.headerActions}>
          {hasTranslation && (
            <LanguageSwitcher
              outputLanguage={outputLanguage}
              activeLanguage={showTranslation ? "en-GB" : outputLanguage}
              onSwitch={lang => {
                const next = new URLSearchParams(searchParams);
                if (lang === "en-GB") next.set("lang", "en-GB");
                else next.delete("lang");
                setSearchParams(next, { replace: true });
              }}
            />
          )}
          {canEdit && !isArchived && (
            <>
              {splitEntry.visible && (
                <button
                  type="button"
                  className={styles.editBpaLink}
                  disabled={splitEntry.disabledReason !== null}
                  aria-describedby={splitEntry.disabledReason ? "split-blocked-reason" : undefined}
                  onClick={openSplit}
                >
                  Split practice area
                </button>
              )}
              {manageTarget && (
                <button
                  type="button"
                  className={styles.editBpaLink}
                  onClick={() =>
                    navigate(
                      ROUTES.research.cmsEditBpa(
                        manageTarget.publicationConfigId,
                        bpaId,
                        manageTarget.editionConfigId,
                      ),
                    )
                  }
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Manage
                </button>
              )}
              {canImport && (
                <>
                  <button
                    type="button"
                    className={styles.editBpaLink}
                    disabled={importBlocked !== null}
                    aria-describedby={importBlocked ? "import-blocked-reason" : undefined}
                    onClick={() => navigate(ROUTES.research.cmsBpaImport(editionId, bpaId))}
                  >
                    Import content
                  </button>
                  {importBlocked && (
                    <span id="import-blocked-reason" className={styles.importBlockedReason}>
                      {importBlocked}
                    </span>
                  )}
                </>
              )}
              {splitEntry.visible && splitEntry.disabledReason && (
                <span id="split-blocked-reason" className={styles.importBlockedReason}>
                  {splitEntry.disabledReason}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {splitOpen && (
        <CmsBpaSplitDialog
          editionId={editionId}
          bpaId={bpaId}
          editionName={editionName}
          onClose={() => setSplitOpen(false)}
        />
      )}

      {pageMergeBanner && !isArchived && (
        <div
          className={`${bannerStyles.banner} ${bannerStyles.info}`}
          role="status"
          data-bpa-banner=""
        >
          <div className={bannerStyles.text}>
            <strong>{pageMergeBanner.title}</strong>
            <span>{pageMergeBanner.body}</span>
          </div>
          <div className={bannerStyles.actions}>
            <Button
              className={importShared.compactButton}
              variant="contained"
              color="primary"
              onClick={() => navigate(pageMergeBanner.href)}
            >
              {pageMergeBanner.actionLabel}
            </Button>
          </div>
        </div>
      )}

      {merged.sourceNames.length > 0 && !mergedDismissed && (
        <div
          className={`${bannerStyles.banner} ${bannerStyles.done}`}
          role="status"
          data-bpa-banner=""
        >
          <div className={bannerStyles.text}>
            <span>{mergedMessage(merged.sourceNames, merged.outcome)}</span>
          </div>
          <div className={bannerStyles.actions}>
            <button
              type="button"
              className={bannerStyles.dismiss}
              onClick={() => setMergedDismissed(true)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {importDone && !isArchived && (
        <ImportDoneBanner
          done={importDone}
          canArchive={canEdit}
          canDelete={canDelete}
          onDismiss={() => setImportDismissed(true)}
        />
      )}

      {isArchived && (
        <ArchivedBanner
          historyState={cmsExportEnabled ? historyState : "ready"}
          onRetry={() => importHistoryQuery.refetch()}
          model={archivedBannerModel(
            importHistoryData?.importHistory ?? [],
            editionBpaId!,
            editionId,
          )}
        />
      )}

      {cmsExportEnabled && editionBpaId && (
        <div className={styles.importHistory}>
          <h2 className={styles.importHistoryHeading}>Import history</h2>
          {historyState === "ready" && (
            <ImportHistoryList
              editionId={editionId}
              editionBpaId={editionBpaId}
              entries={importHistoryData?.importHistory ?? []}
              firmNamesUnavailable={importHistoryData?.firmNamesUnavailable ?? false}
            />
          )}
          {historyState === "loading" && (
            <p className={styles.importHistoryStatus}>Loading import history...</p>
          )}
          {historyState === "failed" && (
            <div className={styles.importHistoryStatus} role="alert">
              <span>Import history couldn't be loaded.</span>{" "}
              <Button
                variant="outlined"
                color="default"
                size="small"
                onClick={() => importHistoryQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          )}
        </div>
      )}

      {noRankingTables ? (
        !isArchived && (
          <CmsEmptyState message="No ranking tables are configured for this practice area. Add ranking tables from the manage screen." />
        )
      ) : (
        <>
          <div className={styles.tabBar} role="tablist" aria-label="Entity type">
            {TABS.filter(tab => (tab.id === "organisations" ? showOrgsTab : showLawyersTab)).map(
              tab => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  <span className={styles.tabCount}>
                    {tab.id === "organisations" ? organisations.length : lawyers.length}
                  </span>
                </button>
              ),
            )}
          </div>

          <div className={styles.wrapper}>
            <div className={styles.searchRow}>
              <div className={styles.searchBarWrap}>
                <SearchBar
                  value={searchQuery}
                  onChange={val => setParam("search", val)}
                  onClear={() => clearParam("search")}
                  placeholder={`Search ${entityLabel}...`}
                />
              </div>
              {!isArchived && (
                <Button variant="outlined" color="primary" onClick={() => openAddModal()}>
                  + Add {isOrgsTab ? "Organisation" : "Lawyer"}
                </Button>
              )}
            </div>

            <AddTargetModal
              type={isOrgsTab ? "organisation" : "lawyer"}
              open={addModalOpen}
              onClose={() => setAddModalOpen(false)}
              onAdd={handleAddTarget}
              adding={false}
              error={addError}
              existingTableIdsByEntityId={existingTableIdsByEntityId}
            />
            {addError && (
              <div className={styles.addError} role="alert">
                {addError}
              </div>
            )}

            <CmsFilterRow
              filters={[
                {
                  key: "submission",
                  label: "Submission",
                  value: submissionFilter,
                  onChange: val => setParam("submission", val),
                  options: [
                    { value: "true", label: "Submitted" },
                    { value: "false", label: "No submission" },
                  ],
                  placeholder: "All",
                },
                ...(isOrgsTab
                  ? [
                      {
                        key: "tier",
                        label: "Ranking",
                        value: tierFilter !== undefined ? String(tierFilter) : "",
                        onChange: (val: string) =>
                          setOrgListFilter({
                            tier: val === "" ? undefined : Number(val),
                          }),
                        options: tierFilterOptions,
                        placeholder: "All",
                      },
                    ]
                  : []),
                {
                  key: "reviewStatus",
                  label: "Review status",
                  value: reviewStatusFilter,
                  onChange: (val: string) =>
                    setOrgListFilter({
                      reviewStatus: val === "" ? undefined : val,
                    }),
                  options: REVIEW_STATUS_FILTER_OPTIONS,
                  placeholder: "All",
                },
                {
                  key: "sort",
                  label: "Sort by",
                  value: sortBy,
                  onChange: val => setParam("sort", val),
                  options: [
                    { value: "name_asc", label: "Name (A-Z)" },
                    { value: "name_desc", label: "Name (Z-A)" },
                    { value: "progress_desc", label: "Progress (High-Low)" },
                    { value: "progress_asc", label: "Progress (Low-High)" },
                  ],
                  placeholder: "",
                },
              ]}
              activeFilters={activeFilters}
              onClearFilter={clearParam}
              onClearAll={clearAll}
            />

            {orgsLoading || lawyersLoading ? (
              <CmsEmptyState message={`Loading ${entityLabel}`} loading />
            ) : isOrgsTab ? (
              filteredOrgs.length === 0 ? (
                organisations.length === 0 ? (
                  <CmsEmptyState message="No organisations have submitted for this practice area yet." />
                ) : (
                  <CmsEmptyState
                    message="No organisations match your filters."
                    action={{ label: "Clear filters", onClick: clearAll }}
                  />
                )
              ) : (
                <CmsGroupedTable
                  groups={orgTableGroups}
                  columns={orgColumns}
                  onRowClick={org => {
                    navigate(ROUTES.research.cmsOrg(editionId, bpaId, org.companyUuid), {
                      state: {
                        bpaName,
                        // Undefined rather than "" so the detail page falls through to its own read.
                        rankingEntityId: rankingEntityId || undefined,
                        rankingEntityName,
                        rankingEntityType: rankingEntityType ?? undefined,
                        editionName,
                        entityName: org.name,
                        companyName: org.name,
                        templateType,
                        currentTier: org.sectionTier,
                        previousTier: org.sectionPreviousTier,
                      },
                    });
                  }}
                  rowKey={orgSectionRowKey}
                  showHeaders
                />
              )
            ) : lawyersError ? (
              <div className={styles.errorMessage} role="alert">
                {lawyersError.message}
              </div>
            ) : filteredLawyers.length === 0 ? (
              lawyers.length === 0 ? (
                <CmsEmptyState message="No lawyers have been recorded for this practice area yet." />
              ) : (
                <CmsEmptyState
                  message="No lawyers match your filters."
                  action={{ label: "Clear filters", onClick: clearAll }}
                />
              )
            ) : (
              <CmsGroupedTable
                groups={lawyerTableGroups}
                columns={lawyerColumns}
                onRowClick={lawyer => {
                  navigate(ROUTES.research.cmsIndividual(editionId, bpaId, lawyer.contactUuid), {
                    state: {
                      bpaName,
                      // Undefined rather than "" so the detail page falls through to its own read.
                      rankingEntityId: rankingEntityId || undefined,
                      rankingEntityName,
                      rankingEntityType: rankingEntityType ?? undefined,
                      editionName,
                      entityName: lawyer.name,
                      templateType,
                      showLawyersTab,
                      companyUuid: lawyer.companyUuid,
                      organisationName: lawyer.organisationName,
                      currentTier: lawyer.sectionTier,
                      previousTier: lawyer.sectionPreviousTier,
                    },
                  });
                }}
                rowKey={lawyerSectionRowKey}
                showHeaders
              />
            )}
          </div>
        </>
      )}
    </CmsLayout>
  );
}
